import { TransportError } from 'ProdantixSdk/core/errors';
import type { FlagSnapshot } from 'ProdantixSdk/core/flag-eval';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';
import type { FlagsResponse } from 'ProdantixSdk/core/types';

export interface FlagsClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

export class FlagsClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: FlagsClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  async fetchAll(distinctId: string): Promise<Record<string, boolean>> {
    return (await this.fetchDecisions(distinctId)).flags;
  }

  async fetchDecisions(distinctId: string): Promise<FlagsResponse> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const url = `${this.host}/v1/flags?distinct_id=${encodeURIComponent(distinctId)}`;
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const response = await fetchImpl(url, {
        headers: { authorization: `Bearer ${this.projectKey}` },
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new TransportError(`flags request failed with status ${response.status}`, response.status);
      }
      const parsed = JSON.parse(await response.text()) as FlagsResponse;
      return { flags: parsed.flags ?? {}, variants: parsed.variants ?? {} };
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  async snapshot(): Promise<FlagSnapshot> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const url = `${this.host}/v1/flags/snapshot`;
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const response = await fetchImpl(url, {
        headers: { authorization: `Bearer ${this.projectKey}` },
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new TransportError(`flag snapshot request failed with status ${response.status}`, response.status);
      }
      const parsed = JSON.parse(await response.text()) as FlagSnapshot;
      const snapshot: FlagSnapshot = { flags: parsed.flags ?? [], generatedAt: parsed.generatedAt };
      if (typeof parsed.socketUrl === 'string' && parsed.socketUrl !== '') {
        snapshot.socketUrl = parsed.socketUrl;
      }
      return snapshot;
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  /** The cohorts a distinct id belongs to, for local evaluation of cohort
   *  conditions against the same membership store the server reads. */
  async memberships(distinctId: string): Promise<string[]> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const url = `${this.host}/v1/flags/memberships?distinct_id=${encodeURIComponent(distinctId)}`;
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const response = await fetchImpl(url, {
        headers: { authorization: `Bearer ${this.projectKey}` },
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new TransportError(`memberships request failed with status ${response.status}`, response.status);
      }
      const parsed = JSON.parse(await response.text()) as { cohorts?: string[] };
      return parsed.cohorts ?? [];
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }
}
